// apps/app/src/services/live.ts
import type { LiveClientMessage, LiveEvent } from '@strategy4kids/protocol';

const BASE = import.meta.env.VITE_API_URL ?? '';

// The WebSocket constructor rejects http(s) schemes. Web builds leave
// VITE_API_URL unset and get a same-origin relative URL (which the browser
// upgrades itself); native builds set an absolute https:// host, so the scheme
// has to be swapped here.
function wsUrl(path: string): string {
  if (!BASE) return path;
  return `${BASE.replace(/^http/, 'ws')}${path}`;
}

export interface ConnectLiveOptions {
  readonly token: string;
  readonly onEvent: (event: LiveEvent) => void;
  readonly onStatus?: (status: 'connected' | 'reconnecting' | 'closed') => void;
  /** Test seam — defaults to the browser WebSocket. */
  readonly socketFactory?: (url: string) => WebSocket;
  /** Reconnect delay in ms (default 1000). */
  readonly reconnectMs?: number;
}

export type LiveEventHandler = ConnectLiveOptions['onEvent'];

export interface LiveClient {
  sendMove(uci: string): void;
  close(): void;
}

export function connectLive(gameId: string, opts: ConnectLiveOptions): LiveClient {
  const url = wsUrl(`/ws/games/${encodeURIComponent(gameId)}`);
  const reconnectMs = opts.reconnectMs ?? 1000;
  let socket: WebSocket;
  let stopped = false;
  let timer: ReturnType<typeof setTimeout> | null = null;

  function open(): void {
    socket = opts.socketFactory ? opts.socketFactory(url) : new WebSocket(url);
    socket.onopen = () => {
      opts.onStatus?.('connected');
      socket.send(JSON.stringify({ type: 'subscribe', token: opts.token } satisfies LiveClientMessage));
    };
    socket.onmessage = (raw) => {
      try {
        opts.onEvent(JSON.parse((raw as MessageEvent<string>).data) as LiveEvent);
      } catch {
        // Malformed frames are ignored — the server never sends them.
      }
    };
    socket.onclose = () => {
      if (stopped) {
        opts.onStatus?.('closed');
        return;
      }
      opts.onStatus?.('reconnecting');
      timer = setTimeout(() => open(), reconnectMs);
    };
    socket.onerror = () => socket.close();
  }
  open();

  return {
    sendMove(uci) {
      socket.send(JSON.stringify({ type: 'move', move: uci } satisfies LiveClientMessage));
    },
    close() {
      stopped = true;
      if (timer) clearTimeout(timer);
      try {
        socket.close();
      } catch {
        // already closed
      }
      opts.onStatus?.('closed');
    },
  };
}
